import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable from '../../components/ui/DataTable';
import { listThirdPartyCOAs, deleteThirdPartyCOA } from '../../services/thirdPartyCoa';
import DeleteConfirmationModal from '../../components/ui/DeleteConfirmationModal';

const ThirdPartyCOALibrary = () => {
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const limit = 10;
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await listThirdPartyCOAs({ search: searchTerm, page: currentPage, limit });
        if (cancelled) return;
        const mapped = (res.data || []).map(x => ({
          id: x._id,
          title: x.title,
          organization: x.organization,
          url: x.url,
          status: x.status === 'active' ? 'Active' : 'Inactive',
          dateCreated: x.createdAt ? new Date(x.createdAt).toLocaleDateString('en-GB') : '',
          lastAction: x.updatedAt ? new Date(x.updatedAt).toLocaleDateString('en-GB') : '',
        }));
        setRows(mapped);
        setTotal(res.pagination?.total || 0);
      } catch (e) {
        console.error('Failed to load third party COAs', e);
        setRows([]);
        setTotal(0);
      }
    })();
    return () => { cancelled = true; };
  }, [searchTerm, currentPage]);

  const columns = [
    { header: 'ID', accessor: 'id', nowrap: true },
    { header: 'Title', accessor: 'title' },
    { header: 'Organization', accessor: 'organization' },
    { header: 'URL', accessor: 'url' },
    { header: 'Status', accessor: 'status', nowrap: true },
    { header: 'Date Created', accessor: 'dateCreated', nowrap: true },
    { header: 'Last Action', accessor: 'lastAction', nowrap: true }
  ];

  const thirdPartyActionMenuItems = [
    {
      action: 'edit',
      label: 'Edit 3rd Party COA',
      icon: (
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
          <path d="M8.25 1.5H6.75C3 1.5 1.5 3 1.5 6.75V11.25C1.5 15 3 16.5 6.75 16.5H11.25C15 16.5 16.5 15 16.5 11.25V9.75" stroke="#73827c" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12.0301 2.2649L6.12006 8.1749C5.90256 8.3924 5.68506 8.8199 5.64006 9.1274L5.30256 10.1533C5.17506 10.8733 5.76006 11.3867 5.84667 11.2867L7.85333 11C8.13333 10.96 8.52667 10.76 8.73333 10.56L13.9867 5.30666C14.8933 4.39999 15.32 3.34666 13.9867 2.01333C12.6533 0.679997 11.6 1.10666 10.6933 2.01333Z" stroke="#73827c" strokeWidth="1.2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M11.3826 2.91248C11.8551 4.36 13.3876 5.61333 15.0876 6.06666" stroke="#73827c" strokeWidth="1.2" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )
    },
    {
      action: 'delete',
      label: 'Delete 3rd Party COA',
      icon: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M2 4H3.33333H14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M12.6667 4V13.3333C12.6667 13.687 12.5262 14.0261 12.2762 14.2761C12.0261 14.5262 11.687 14.6667 11.3333 14.6667H4.66667C4.31305 14.6667 3.97391 14.5262 3.72386 14.2761C3.47381 14.0261 3.33334 13.687 3.33334 13.3333V4M5.33334 4V2.66667C5.33334 2.31305 5.47381 1.97391 5.72386 1.72386C5.97391 1.47381 6.31305 1.33333 6.66667 1.33333H9.33334C9.68696 1.33333 10.0261 1.47381 10.2762 1.72386C10.5262 1.97391 10.6667 2.31305 10.6667 2.66667V4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      ),
      danger: true
    }
  ];

  const renderCell = (accessor, row) => {
    switch (accessor) {
      case 'id': {
        const fullId = row.id || '';
        const truncatedId = fullId.length > 5 ? fullId.substring(0, 5) + '...' : fullId;
        return (
          <span className="text-sm text-gray-900 font-mono">{truncatedId}</span>
        );
      }
      case 'url':
        return (
          <a
            href={row.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-blue-600 hover:text-blue-800 underline"
          >
            {row.url.length > 50 ? `${row.url.substring(0, 50)}...` : row.url}
          </a>
        );
      case 'status':
        return (
          <span className={`px-2.5 py-1 inline-flex items-center gap-1 text-xs font-medium rounded-full ${
            row.status === 'Active' ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-50 text-gray-600'
          }`}>
            <span className="w-1.5 h-1.5 rounded-full bg-current"></span>
            {row.status}
          </span>
        );
      default:
        return <span className="text-sm text-gray-900">{row[accessor]}</span>;
    }
  };

  const handleRowAction = async (action, row) => {
    const id = typeof row === 'string' ? row : row?.id || row?._id;
    if (!id) return;

    if (action === 'edit') {
      navigate(`/create-third-party-coa/${id}`);
    } else if (action === 'delete') {
      const target = rows.find(r => r.id === id) || { id };
      setRowToDelete(target);
      setIsDeleteModalOpen(true);
    }
  };

  const handleDelete = async () => {
    if (!rowToDelete) return;
    try {
      await deleteThirdPartyCOA(rowToDelete.id);
      const res = await listThirdPartyCOAs({ search: searchTerm, page: currentPage, limit });
      const mapped = (res.data || []).map(x => ({
        id: x._id,
        title: x.title,
        organization: x.organization,
        url: x.url,
        status: x.status === 'active' ? 'Active' : 'Inactive',
        dateCreated: x.createdAt ? new Date(x.createdAt).toLocaleDateString('en-GB') : '',
        lastAction: x.updatedAt ? new Date(x.updatedAt).toLocaleDateString('en-GB') : '',
      }));
      setRows(mapped);
      setTotal(res.pagination?.total || 0);
    } catch (error) {
      console.error('Delete failed', error);
      alert('Failed to delete 3rd Party COA. Please try again.');
    }
    setIsDeleteModalOpen(false);
    setRowToDelete(null);
  };

  return (
    <div className="space-y-6">
      {/* Page Title and Actions */}
      <div className="flex items-center justify-between">
        <h1 className="text-[28px] font-semibold text-[#0a1f17] font-inter tracking-[-0.56px] leading-[1.4]">
          3rd Party COA Library
        </h1>
        <div className="flex items-center gap-3">
          <div className="relative">
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => { setCurrentPage(1); setSearchTerm(e.target.value); }}
              className="w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
            />
            <svg className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <button
            onClick={() => navigate('/create-third-party-coa')}
            className="px-5 py-2 bg-gradient-to-r from-[#40c191] to-[#5ed58d] text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity shadow-sm"
          >
            Add 3rd Party COA
          </button>
        </div>
      </div>

      {/* Data Table */}
      <DataTable
        columns={columns}
        data={rows}
        rowsPerPage={rows.length || 1}
        showPagination={false}
        actionMenuItems={thirdPartyActionMenuItems}
        onRowAction={handleRowAction}
        renderCell={renderCell}
      />

      {/* Server-side pagination */}
      <div className="flex items-center justify-center gap-2">
        <button
          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
          disabled={currentPage === 1}
          className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'}`}
        >
          Previous
        </button>
        <span className="text-sm text-gray-600">
          Page {currentPage} of {Math.max(1, Math.ceil(total / limit))}
        </span>
        <button
          onClick={() => setCurrentPage(p => (p < Math.ceil(total / limit) ? p + 1 : p))}
          disabled={currentPage >= Math.ceil(total / limit)}
          className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${currentPage >= Math.ceil(total / limit) ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'}`}
        >
          Next
        </button>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => { setIsDeleteModalOpen(false); setRowToDelete(null); }}
        onConfirm={handleDelete}
        title="Delete 3rd Party COA"
        message="Are you sure you want to delete this 3rd Party COA? This action cannot be undone."
        itemName={rowToDelete ? (rowToDelete.title || rowToDelete.id || '') : ''}
      />
    </div>
  );
};

export default ThirdPartyCOALibrary;

